/**
 * Module dependencies.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from 'app/components/ui/alert-dialog';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { useCrudDelete } from 'app/hooks/requests/crud/use-crud-delete';
import { useCrudList } from 'app/hooks/requests/crud/use-crud-list';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CrudTemplate } from './form-types';
import { Table } from 'app/components/organisms/table/table';
import { Button } from 'app/components/atoms/button/button';
import { CrudFilters } from './form-filters/crud-filters';
import { Wrapper } from 'app/components/atoms/wrapper/wrapper';
import { Loading } from 'app/components/atoms/loading/loading';
import { Pencil, Trash2 } from 'lucide-react';
import { useDebounceLoading } from 'app/hooks/use-debounce';
import { useCanEditProject } from 'app/hooks/use-project-role';
import { cn } from 'app/lib/utils';
import { Drawer } from 'app/components/organisms/drawer/drawer';
import { CrudEdit } from './crud-edit';
import { CrudAdd } from './crud-add';

/**
 * `Props` type.
 */

type Props = {
  template: CrudTemplate;
  nestedParams?: any;
};

/**
 * `ExpandedRow` component.
 */

function ExpandedRow({ extras, item, expanded }: { item: any; extras: any; expanded: boolean }) {
  const list = extras.template.list;
  const columns = list.expandedColumns;
  const rowKey = list.expandedRowKey ?? 'id';
  const dataKey = list.expandedDataKey;
  const data = useMemo(() => {
    if (typeof dataKey === 'function') {
      return dataKey(item);
    }

    return item[dataKey];
  }, [dataKey, item]);

  if (!expanded) {
    return null;
  }

  return <Table className={'bg-transparent'} columns={columns} dataSource={data} rowKey={rowKey} />;
}

/**
 * `ExpandedRowTable` component.
 */

function ExpandedRowTable({ extras, item, expanded }: { item: any; extras: any; expanded: boolean }) {
  const template = extras.template.list.expandedTemplate;
  const localList = !template.list.endpoint;
  const nestedParams = useMemo(() => {
    return {
      ...(template.list?.nestedParams?.(item) ?? {}),
      ...(localList ? { item } : {})
    };
  }, [item, localList, template.list]);

  if (!expanded) {
    return null;
  }

  if (!localList && !nestedParams) {
    throw new Error('`nestedParams` is required for `expandedTemplate`');
  }

  return (
    <div className={'!min-h-0 rounded-b-lg bg-transparent pb-10'}>
      <CrudList nestedParams={nestedParams} template={template} />
    </div>
  );
}

/**
 * Export `CrudList` component.
 */

export function CrudList<T>({ template, nestedParams }: Props): JSX.Element {
  const [translate] = useTranslation();
  const routerParams = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const params = useMemo(
    () => ({
      ...routerParams,
      ...(nestedParams ?? {})
    }),
    [nestedParams, routerParams]
  );

  const canEditProject = useCanEditProject();
  const editIsDrawer = template.edit?.displayMode === 'drawer';
  const addIsDrawer = template.add?.displayMode === 'drawer';
  const [editingId, setEditingId] = useState<any>(null);
  const [addOpen, setAddOpen] = useState<boolean>(false);
  const bulkEdit = template.list?.bulkEdit;
  const [bulkMode, setBulkMode] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);
  const [bulkDrawerOpen, setBulkDrawerOpen] = useState<boolean>(false);

  const toggleSelected = useCallback((id: string | number) => {
    setSelectedIds(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]));
  }, []);

  const exitBulkMode = useCallback(() => {
    setBulkMode(false);
    setSelectedIds([]);
    setBulkDrawerOpen(false);
  }, []);
  const [pageSize, changePageSize] = useState(template.list?.pagination?.pageSize ?? 10);
  const lastTotal = useRef<number | undefined>();
  const { mutate: deleteAction, isLoading } = useCrudDelete(template, params);
  const [query, setQuery] = useState<any>();
  const { data, refetch, ...restQuery } = useCrudList<T>(template, params, query);
  const appendFilters = useCallback(
    (values: any) => {
      const query = {
        ...(template.list?.append ?? {}),
        ...values
      };

      if (template.list?.pagination) {
        query.page = searchParams.get('page') ?? 1;
        query.pageSize = pageSize;
      }

      query.execute = true;

      setQuery((data: any) => {
        if (JSON.stringify(data) === JSON.stringify(query)) {
          return data;
        }

        return query;
      });
    },
    [pageSize, searchParams, template.list?.append, template.list?.pagination]
  );

  const page = searchParams.get('page') ?? 1;
  // Only surface the loading overlay on the initial load (no data yet), not on
  // background refetches — otherwise the spinner lingers over the list.
  const isLoadingData = useDebounceLoading(restQuery.isLoading, 50, 350);

  useEffect(() => {
    if (!(data as any)?.total) {
      return;
    }

    if (!lastTotal.current) {
      lastTotal.current = (data as any).total;

      return;
    }
    if (lastTotal.current !== (data as any)?.total) {
      lastTotal.current = (data as any).total;

      setSearchParams(params => {
        params.set('page', '1');

        return params;
      });
    }
  }, [data, setSearchParams]);

  useEffect(() => {
    setQuery((query: any) => ({
      ...query,
      page
    }));
  }, [page, refetch]);

  useEffect(() => {
    if (query) {
      refetch();
    }
  }, [query, refetch]);

  const columns = useMemo(() => {
    const columns = [...(template.list?.columns ?? [])];

    columns.push({
      title: translate('common.table.columns.actions'),
      key: 'actions',
      style: columns?.[0].style,
      render: (item: any) => {
        const _canEditTemplate =
          typeof template.list?.canEdit === 'function' ? template.list?.canEdit(item) : template.list?.canEdit;
        const _canRemoveTemplate =
          typeof template.list?.canRemove === 'function' ? template.list?.canRemove(item) : template.list?.canRemove;
        const _canEdit = _canEditTemplate && canEditProject;
        const _canRemove = _canRemoveTemplate && canEditProject;

        const isSelected = selectedIds.includes(item.id);

        return (
          <div className={'flex justify-start gap-4'}>
            {_canEdit &&
              template?.edit &&
              (template.edit.displayMode === 'drawer' ? (
                <Button
                  disabled={bulkMode && !isSelected}
                  icon={<Pencil className={'h-4 w-4'} />}
                  onClick={event => {
                    event?.stopPropagation();

                    if (bulkMode) {
                      if (isSelected) {
                        setBulkDrawerOpen(true);
                      }

                      return;
                    }

                    setEditingId(item.id);
                  }}
                  size={'small'}
                />
              ) : (
                <Link
                  onClick={event => {
                    event.stopPropagation();
                  }}
                  to={resolveAppPath(template.edit?.route as any, { ...(params ?? {}), id: item.id })}
                >
                  <Button icon={<Pencil className={'h-4 w-4'} />} size={'small'} />
                </Link>
              ))}

            <div className={cn('flex gap-4', bulkMode && 'pointer-events-none opacity-40')}>
              {_canRemove && template.remove && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      danger
                      icon={<Trash2 className={'h-4 w-4'} />}
                      onClick={(event: any) => event.stopPropagation()}
                      size={'small'}
                    />
                  </AlertDialogTrigger>

                  <AlertDialogContent onClick={(event: any) => event.stopPropagation()}>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{translate('removeAction')}</AlertDialogTitle>
                    </AlertDialogHeader>

                    <AlertDialogFooter>
                      <AlertDialogCancel>{translate('common.actions.cancel')}</AlertDialogCancel>

                      <AlertDialogAction
                        className={'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90'}
                        disabled={isLoading}
                        onClick={(event: any) => {
                          event.stopPropagation();
                          deleteAction(item.id);
                        }}
                      >
                        {translate('common.actions.remove')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}

              {template.list?.renderActions?.(item, template, refetch)}
            </div>
          </div>
        );
      }
    });

    return columns;
  }, [bulkMode, canEditProject, deleteAction, isLoading, params, refetch, selectedIds, template, translate]);

  const addInitialValues = useMemo(
    () => template.add?.drawerInitialValues?.({ params, searchParams }),
    [template.add, params, searchParams]
  );

  const WrapperComponent = template.list?.WrapperComponent ?? Wrapper;
  const expandedRowRender = useMemo(() => {
    if (template?.list?.expandedColumns) {
      return ExpandedRow;
    }

    if (template?.list?.expandedTemplate) {
      return ExpandedRowTable;
    }
  }, [template?.list?.expandedColumns, template?.list?.expandedTemplate]);

  const onClickLine = useMemo(() => {
    if (!template.list?.clickLine) {
      return undefined;
    }

    return (item: any) => {
      template?.list?.clickLine?.(item, { navigate });
    };
  }, [navigate, template.list]);

  return (
    <WrapperComponent isLoading={restQuery.isLoading} refetch={refetch} template={template}>
      <CrudFilters
        appendFilters={appendFilters}
        bulkEdit={bulkEdit}
        bulkMode={bulkMode}
        hasNested={!!nestedParams}
        onAddDrawer={addIsDrawer ? () => setAddOpen(true) : undefined}
        onEnterBulk={() => setBulkMode(true)}
        onExitBulk={exitBulkMode}
        params={params}
        query={query}
        refresh={refetch}
        selectedCount={selectedIds.length}
        template={template}
      />

      <Loading isLoading={isLoading}>
        <Table
          changePageSize={changePageSize}
          className={'bg-transparent'}
          columns={columns}
          dataSource={(data as any)?.results ?? []}
          expandedExtras={{ template }}
          expandedRowRender={expandedRowRender}
          footer={template?.list?.renderLastLine?.({
            data: (data as any)?.results,
            total: (data as any)?.total,
            summary: (data as any)?.summary,
            columns,
            template,
            refetch
          })}
          isLoading={isLoadingData}
          onClickLine={bulkMode ? (item: any) => toggleSelected(item.id) : onClickLine}
          pagination={
            template.list?.pagination && {
              total: (data as any)?.total,
              pageSize: pageSize,
              pageSizeOptions: template.list?.pagination?.pageSizeOptions
            }
          }
          rowClassName={
            bulkMode
              ? (item: any) => (selectedIds.includes(item.id) ? '[&_.cell]:!bg-[#e6f7ff]' : undefined)
              : undefined
          }
          rowKey={'id'}
          sortable={template?.list?.sortable}
          style={template.list?.style}
          wrapperClassName={cn(!!nestedParams && '!min-h-0 rounded-b-lg bg-transparent pb-10')}
        />
      </Loading>

      {editIsDrawer && template.edit && (
        <Drawer
          isOpen={editingId != null}
          onClose={() => setEditingId(null)}
          title={template.edit.submitLabel}
          width={template.edit.drawerWidth}
        >
          {editingId != null ? (
            <CrudEdit
              id={editingId}
              onDone={() => {
                setEditingId(null);
                refetch();
              }}
              template={template}
            />
          ) : (
            <span />
          )}
        </Drawer>
      )}

      {bulkEdit && (
        <Drawer isOpen={bulkDrawerOpen} onClose={() => setBulkDrawerOpen(false)} width={640}>
          {bulkDrawerOpen ? (
            (bulkEdit.renderDrawer({
              items: ((data as any)?.results ?? []).filter((row: any) => selectedIds.includes(row.id)),
              onClose: () => setBulkDrawerOpen(false),
              onDone: () => {
                refetch();
                exitBulkMode();
              }
            }) as JSX.Element)
          ) : (
            <span />
          )}
        </Drawer>
      )}

      {addIsDrawer && template.add && (
        <Drawer
          isOpen={addOpen}
          onClose={() => setAddOpen(false)}
          title={template.add.submitLabel}
          width={template.add.drawerWidth}
        >
          {addOpen ? (
            <CrudAdd
              initialValues={addInitialValues}
              onDone={() => {
                setAddOpen(false);
                refetch();
              }}
              template={template}
            />
          ) : (
            <span />
          )}
        </Drawer>
      )}
    </WrapperComponent>
  );
}
